import React, { useState, useEffect } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress, Box } from '@mui/material';
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import { useMapContext } from '../../contexts/MapContext';
import ProgressPanel from "./ProgressPanel";
import {Amplify} from "aws-amplify";
import outputs from "../../amplifyconfiguration.json";
import {generateClient} from "aws-amplify/api";
import { useLIDContext } from "../../contexts/LIDContext";

Amplify.configure(outputs);
const client = generateClient({
    authMode: "userPool",
    region: outputs.aws_appsync_region,
    url: outputs.aws_appsync_graphqlEndpoint,
});

const RunModel = () => {
    const [isModelRunningDialogOpen, setIsModelRunningDialogOpen] = useState(false);
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { 
        inpFile, 
        outFileExists,
        planIsRunning,
        setPlanIsRunning,
        devPlanID,
        runModelGuard,
    } = useMapContext();
    const {
        devPlanContent,
    } = useLIDContext();

    const initProgress = async () => {
        const stormOptions = ['Aug16']; // Switching this back to just the one storm.
        try {
            for (const storm of stormOptions) {
                const result = await client.graphql({
                    query: `
                        mutation initProgress($id: ID = "", $progress: Int = 10) {
                          updateDevelopmentPlan(input: {id: $id, progress_${storm}: $progress}) {
                            progress_${storm}
                          }
                        }
                    `,
                    variables: {id: devPlanID, progress : 0},
                });
                console.log("Successfully initialized model progress");
            }
        } catch (error) {
            console.log("Error in initializing model progress: " + error);
        }
    }

    const handleMakeItRain = async () => {
        if (!inpFile) {
            console.error('No inp file available');
            return;
        }

        const fileName = inpFile.replace('.inp', '');

        const url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/run-model/${fileName}`;
        try {
            const response = await fetch(url, {
                method: 'GET',
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Model run successful:', data);
                setPlanIsRunning(true);
                await initProgress();
                setIsModelRunningDialogOpen(true);
                setIsConfirmationDialogOpen(false);
            } else {
                console.error('Error running model:', response.statusText);
            }
        } catch (error) {
            console.error('Error running model:', error);
        }
    };

    return (
        <div>
            {!outFileExists && !runModelGuard &&(
                <Box
                    sx={{ width: '30px', margin: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor : 'transparent'}}
                    aria-label="Model is running"
                >
                    <CircularProgress />
                </Box>

            )}
            {runModelGuard && !planIsRunning && !outFileExists && Object.keys(devPlanContent).length > 0 && (
                <Button
                    variant="contained"
                    startIcon={<ThunderstormIcon />}
                    style={{
                        zIndex: 1000,
                        position: 'absolute',
                        top: '10px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#94cfec',
                        color: '#000',
                        borderRadius: '25px',
                        padding: '10px 20px',
                    }}
                    onClick={() => setIsConfirmationDialogOpen(true)} // Open confirmation dialog instead
                >
                    Make it Rain
                </Button>
            )}

            {/* Display progress panel if plan is not done running */}
            {runModelGuard && !outFileExists && (
                <ProgressPanel/>
            )}

            {/* Confirmation Dialog */}
            <Dialog
                open={isConfirmationDialogOpen}
                onClose={() => setIsConfirmationDialogOpen(false)}
            >

                <DialogTitle>Confirm Model Run</DialogTitle>
                <DialogContent>
                    {isLoading ? (
                        // A loading circle is shown when the model is running.
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <CircularProgress aria-label="Model run in progress" />
                        </Box>
                    ) : (
                        <DialogContentText>
                            This will run the model. It will take about 45 minutes. You'll receive an email when it's done. Are you sure?
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsConfirmationDialogOpen(false)}>Go back</Button>
                    <Button onClick={handleMakeItRain} autoFocus disabled={isLoading}>Run it!</Button>
                </DialogActions>
            </Dialog>
            
            <Dialog
                open={isModelRunningDialogOpen}
                onClose={() => setIsModelRunningDialogOpen(false)}
            >
                <DialogTitle>Model Running</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The model is now running. We'll send you an email when it's done.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsModelRunningDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default RunModel;